// @flow
import { CART } from 'services/actions/cartActions';

import type { Action } from 'services/actions/Action.type';
import type {
  UpdateCartResponse,
  CreditResponse,
  CartQuantities,
} from '../../sharedTypes';

export type CartState = $ReadOnly<{|
  ...UpdateCartResponse,
  ...CreditResponse,
  variantIdsBeingUpdated: Set<string>,
  cartLoadFailure: boolean,
  prevPurchasedVariantQuantities: CartQuantities,
  fetchingCart: boolean,
  fetchingPreviousCart: boolean,
  prevPurchasedCount: number,
  isShippingWithBox: boolean,
|}>;

export const cartInitialState = {
  variantIds: [],
  variantQuantities: {},
  variantIdsBeingUpdated: new Set<string>(),
  initialAddonCredit: 0,
  initialPurchaseCredit: 0,
  // TODO: Convert these and all other currency-related values to numbers
  // (this requires back-end changes too)
  shippingFee: '',
  additionalShippingFee: '',
  tax: '',
  taxLabel: '',
  amountToFreeShipping: '',
  taxPercentage: '',
  cartText: '',
  cartLoadFailure: false,
  prevPurchasedVariantQuantities: {},
  fetchingCart: true,
  fetchingPreviousCart: true,
  prevPurchasedCount: 0,
  variantCadenceTypes: {},
};

export function cartReducer(
  state: CartState = cartInitialState,
  action: Action
): CartState {
  switch (action.type) {
    case CART.SET_CART_DATA: {
      return {
        ...state,
        ...action.payload,
        fetchingCart: false,
      };
    }
    case CART.SET_PREV_PURCHASED_VARIANTS: {
      return {
        ...state,
        prevPurchasedVariantQuantities: action.payload,
      };
    }
    case CART.END_LOAD_PREVIOUS_CART: {
      return {
        ...state,
        fetchingPreviousCart: false,
      };
    }
    case CART.REQUEST_ADD_TO_CART:
    case CART.REQUEST_ADD_TO_PUBLIC_CART: {
      const { variantId } = action;
      const { variantIdsBeingUpdated } = state;
      return {
        ...state,
        variantIdsBeingUpdated: setUpdateStart(
          variantIdsBeingUpdated,
          variantId
        ),
      };
    }
    case CART.REQUEST_REMOVE_FROM_CART: {
      const { variantId } = action;
      const { variantIdsBeingUpdated } = state;

      return {
        ...state,
        variantIdsBeingUpdated: setUpdateStart(
          variantIdsBeingUpdated,
          variantId
        ),
      };
    }
    case CART.REQUEST_CLEAR_VARIANT_ITEMS_FROM_CART: {
      const { variantId } = action;
      const { variantIdsBeingUpdated } = state;

      return {
        ...state,
        variantIdsBeingUpdated: setUpdateStart(
          variantIdsBeingUpdated,
          variantId
        ),
      };
    }
    case CART.RECEIVE_UPDATE_CART: {
      const { variantId, payload } = action;
      const { variantIdsBeingUpdated } = state;
      return {
        ...state,
        variantIdsBeingUpdated: setUpdateEnd(variantIdsBeingUpdated, variantId),
        variantIds: payload.variantIds,
        variantQuantities: payload.variantQuantities,
        shippingFee: payload.shippingFee,
        additionalShippingFee: payload.additionalShippingFee,
        tax: payload.tax,
        variantCadenceTypes: payload.variantCadenceTypes,
      };
    }
    case CART.FAILED_CART_REQUEST: {
      return {
        ...state,
        cartLoadFailure: true,
        fetchingCart: false,
      };
    }
    case CART.RECEIVE_UPDATE_PUBLIC_CART: {
      const { variantId } = action;
      const { variantIdsBeingUpdated } = state;
      return {
        ...state,
        variantIdsBeingUpdated: setUpdateEnd(variantIdsBeingUpdated, variantId),
      };
    }
    case CART.RECEIVE_UPDATE_CART_ERROR:
    case CART.RECEIVE_UPDATE_PUBLIC_CART_ERROR: {
      const { variantId } = action;
      const { variantIdsBeingUpdated } = state;
      return {
        ...state,
        variantIdsBeingUpdated: setUpdateEnd(variantIdsBeingUpdated, variantId),
      };
    }

    default: {
      return state;
    }
  }
}

function setUpdateStart(variantIdsBeingUpdated, variantId) {
  const newVariantIdsBeingUpdated = new Set(variantIdsBeingUpdated);
  return newVariantIdsBeingUpdated.add(variantId);
}

function setUpdateEnd(variantIdsBeingUpdated, variantId) {
  const newVariantIdsBeingUpdated = new Set(variantIdsBeingUpdated);
  newVariantIdsBeingUpdated.delete(variantId);
  return newVariantIdsBeingUpdated;
}
